import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiFacebook, FiInstagram, FiTwitter, FiYoutube, FiArrowUp } = FiIcons;

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { label: 'About Us', href: '#about' },
    { label: 'Rooms & Rates', href: '#rooms' },
    { label: 'Safari Packages', href: '#safari' },
    { label: 'Facilities', href: '#facilities' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Contact', href: '#contact' }
  ];

  const safariLinks = [
    { label: 'Morning Safari', href: '#safari' },
    { label: 'Evening Safari', href: '#safari' },
    { label: 'Full Day Safari', href: '#safari' },
    { label: 'Ranger Package', href: '#rooms' },
    { label: 'Photography Tours', href: '#safari' },
    { label: 'Elephant Transit Home', href: '#about' }
  ];

  const policies = [
    { label: 'Booking Policy', href: '#' },
    { label: 'Cancellation Policy', href: '#' },
    { label: 'Privacy Policy', href: '#' },
    { label: 'Terms & Conditions', href: '#' },
    { label: 'COVID-19 Guidelines', href: '#' },
    { label: 'Safari Safety Rules', href: '#' }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-black text-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 right-10 w-40 h-40 liquid-gradient rounded-full opacity-10 morphing-blob"
          animate={{ 
            x: [0, 30, 0], 
            y: [0, -20, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-32 h-32 bg-gradient-to-br from-orange-400/20 to-red-500/20 rounded-full morphing-blob"
          animate={{ 
            x: [0, -25, 0], 
            y: [0, 15, 0],
            rotate: [0, 180, 360]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Main Footer Content */}
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-12 mb-12">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 liquid-gradient rounded-full flex items-center justify-center morphing-blob">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <div>
                <h3 className="text-2xl font-playfair font-bold">Hotel Athgira</h3>
                <p className="text-orange-300 text-sm">Original Safari Basecamp</p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6 font-inter leading-relaxed">
              Since 2010, we've been the trusted safari basecamp in Udawalawe, 
              offering authentic hospitality with the expertise of Niluka Safari Services since 1980.
            </p>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiPhone} className="w-4 h-4 text-orange-400" />
                <a href="tel:+94472226621" className="text-gray-300 hover:text-orange-300 transition-colors font-inter">
                  +94 47 2226621
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMail} className="w-4 h-4 text-orange-400" />
                <a href="mailto:info@hotelathgira.com" className="text-gray-300 hover:text-orange-300 transition-colors font-inter">
                  info@hotelathgira.com
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMapPin} className="w-4 h-4 text-orange-400" />
                <span className="text-gray-300 font-inter">Udawalawe Road, Udawalawe</span>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-xl font-playfair font-bold mb-6">Quick Links</h4>
            <div className="space-y-3">
              {quickLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="block text-gray-300 hover:text-orange-300 transition-colors font-inter"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Safari Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-xl font-playfair font-bold mb-6">Safari Services</h4>
            <div className="space-y-3">
              {safariLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="block text-gray-300 hover:text-orange-300 transition-colors font-inter"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Policies & Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-xl font-playfair font-bold mb-6">Policies & Info</h4>
            <div className="space-y-3 mb-6">
              {policies.map((policy) => (
                <a
                  key={policy.label}
                  href={policy.href}
                  className="block text-gray-300 hover:text-orange-300 transition-colors font-inter"
                >
                  {policy.label}
                </a>
              ))}
            </div>

            {/* Social Media */}
            <h5 className="font-semibold mb-3 font-inter">Follow Us</h5>
            <div className="flex space-x-3">
              {[FiFacebook, FiInstagram, FiTwitter, FiYoutube].map((Icon, index) => (
                <motion.a
                  key={index}
                  href="#"
                  className="w-10 h-10 glass-effect-dark rounded-full flex items-center justify-center text-gray-300 hover:text-orange-300 transition-colors"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SafeIcon icon={Icon} className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Newsletter Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="glass-effect-dark rounded-2xl p-8 mb-12"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h4 className="text-2xl font-playfair font-bold mb-3">Stay Connected</h4>
              <p className="text-gray-300 font-inter">
                Subscribe to our newsletter for safari tips, seasonal updates, and exclusive offers 
                from the heart of Udawalawe.
              </p>
            </div>
            <div className="flex space-x-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20 transition-all font-inter"
              />
              <motion.button
                className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-3 rounded-xl font-semibold ripple-effect"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Subscribe
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-gray-700">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-gray-400 text-sm mb-4 md:mb-0 font-inter"
          >
            <p>
              © 2025 Hotel Athgira. All rights reserved. | 
              <span className="text-orange-400"> Designed and Created by Rajantha</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex items-center space-x-4"
          >
            <span className="text-gray-400 text-sm font-inter">Back to top</span>
            <motion.button
              onClick={scrollToTop}
              className="w-10 h-10 glass-effect-dark rounded-full flex items-center justify-center text-gray-300 hover:text-orange-300 transition-colors"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.9 }}
            >
              <SafeIcon icon={FiArrowUp} className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>

        {/* Sister Properties Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-8 text-center"
        >
          <p className="text-gray-500 text-xs font-inter">
            Explore our sister properties: 
            <a href="#" className="text-orange-400 hover:text-orange-300 transition-colors ml-1">Athgira River Camp</a> | 
            <a href="#" className="text-orange-400 hover:text-orange-300 transition-colors ml-1">Elephant Trail</a>
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;